import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { exposeEventToPartner } from '../../Helpers/events';
import { setParentPageCookies } from '../../Lib/redux/slices/cookies';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import {
  setCampaignId,
  setHeaderEnabled,
  setPageUrl,
  setReferrerUrl,
} from '../../Lib/redux/slices/formConfig';
import { setLogRocketData } from '../../Lib/redux/slices/logRocket';
import { setShouldNavigateBackward } from '../../Lib/redux/slices/navigationControls';
import { setOneTrustLocale } from '../../Lib/redux/slices/oneTrustLocale';
import {
  setAllInitialMessagesArrived,
  setAutoScrollEnabled,
  setDynamicHeightEnabled,
  setFontOverride,
  setIFrameId,
  setIsConsentStatementAboveNavigation,
  setMaximumWidgetHeight,
  setMinimumWidgetHeight,
  addRegisteredEventHandlers,
  setTestMode,
  setUseCidFromURL,
  setWidgetLabel,
  setWidgetResizeDuration,
  addRegisteredEventHandler,
} from '../../Lib/redux/slices/optionalFormConfig';
import { setIsBounceNavigationAnimationEnabled } from '../../Lib/redux/slices/features';
import { setAdvancePageStatus } from '../../Lib/redux/slices/sessionDataPopulation';
import { setResubmit } from '../../Lib/redux/slices/submission';
import { setPaletteOverrides } from '../../Lib/redux/slices/theme';
import {
  parseSessionDataAndPopulateFormFields,
  extractParentPageTrustedFormOutOfSessionData,
} from './useBrowserMessagesHelpers';
import { setEventPublicKeyString } from '../../Lib/redux/slices/publicKeys';
import { updateHasStatementGraduallyDisappeared } from '../../Lib/redux/slices/legalStatement';
import { handleClickBackSideEffects } from '../../Components/NavigationBar/navigationBar.helpers';
import { getUrlHashParams } from '../../Helpers/getUrlHashParams';
import trustedFormConfig from '../../Data/Maps/trustedFormConfig';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import {
  updateIdentities,
  setAnuraData,
} from '../../Lib/redux/slices/userIdentities';
import { setComponentExperiments } from '../../Lib/redux/slices/experimentation';
import { incrementMetric } from '../../Services/Datadog';

const useBrowserMessages = () => {
  const dispatch = useDispatch();
  const { iFrameId } = useSelector((store) => store.optionalFormConfig);
  const {
    isExternalPartnerForm,
    isTrackingReferrerEnabled,
    countryCode,
    formId,
    submissionPageNumber,
  } = useSelector((store) => store.formConfig);
  const { sessionDataPopulationStatus } = useSelector(
    (store) => store.sessionDataPopulation
  );
  const components = useSelector((store) => store.components);
  const { currentPage, totalPages } = useSelector((i) => i.navigation);
  const isPenultimatePage =
    typeof submissionPageNumber === 'number'
      ? currentPage === submissionPageNumber
      : currentPage + 1 === totalPages;
  const {
    isDynamicHeightEnabled,
    minimumWidgetHeight,
    isConsentStatementAboveNavigation,
  } = useSelector((store) => store.optionalFormConfig);
  const { hasStatementGraduallyDisappeared, statement } = useSelector(
    (store) => store.legalStatement
  );
  const [externalSessionDataJSON, setExternalSessionDataJSON] = useState(false);

  const [
    hasSentBrowserMessagesRegisteredEvent,
    setHasSentBrowserMessagesRegisteredEvent,
  ] = useState(false);

  useEffect(() => {
    window.onmessage = (event) => {
      const { data } = event;
      if (typeof data === 'string') {
        if (data.startsWith('pageUrl:')) {
          const pageUrl = data.slice(8);
          dispatch(setPageUrl({ pageUrl }));
        } else if (data.startsWith('referrerUrl:')) {
          if (isTrackingReferrerEnabled) {
            const referrerUrl = data.slice(12);
            dispatch(setReferrerUrl({ referrerUrl }));
          }
        } else if (data.startsWith('isLogRocketSessionRecordingRequired:')) {
          if (data === 'isLogRocketSessionRecordingRequired:true') {
            dispatch(setLogRocketData({ isSessionRecordingRequired: true }));
          }
        } else if (data.startsWith('campaignId:')) {
          const campaignId = data.slice(11);
          dispatch(setCampaignId({ campaignId }));
        } else if (data.startsWith('headerEnabled:')) {
          const headerEnabled = data.slice(14) === 'true';
          dispatch(setHeaderEnabled({ headerEnabled }));
        } else if (data.startsWith('resubmit:')) {
          const { shouldResubmit, replaceAncestorSid } = JSON.parse(
            data.slice(9)
          );
          if (shouldResubmit === true) {
            dispatch(setResubmit({ shouldResubmit, replaceAncestorSid }));
          }
        } else if (data.startsWith('paletteOverrides:')) {
          const paletteOverrides = JSON.parse(data.slice(17));
          dispatch(setPaletteOverrides(paletteOverrides));
        } else if (data.startsWith('widgetLabel:')) {
          const widgetLabel = data.slice(12);
          dispatch(setWidgetLabel({ widgetLabel }));
        } else if (data.startsWith('isConsentStatementAboveNavigation:')) {
          dispatch(
            setIsConsentStatementAboveNavigation({
              isConsentStatementAboveNavigation: data.slice(34) === 'true',
            })
          );
        } else if (data.startsWith('testMode:')) {
          const testMode = data.slice(9) === 'true';
          dispatch(setTestMode({ testMode }));
        } else if (data.startsWith('externalSessionData:')) {
          setExternalSessionDataJSON(data.slice(20));
        } else if (data.startsWith('iFrameId:')) {
          const iFrameIdString = data.slice(9);
          dispatch(setIFrameId({ iFrameId: iFrameIdString }));
        } else if (data.startsWith('fontOverride:')) {
          const fontOverride = JSON.parse(data.slice(13));
          dispatch(setFontOverride({ fontOverride }));
        } else if (data.startsWith('registeredEventHandlers:')) {
          const registeredEventHandlers = JSON.parse(data.slice(24));
          dispatch(addRegisteredEventHandlers({ registeredEventHandlers }));
        } else if (data.startsWith('registerEventHandler:')) {
          const eventHandler = data.slice(21);
          dispatch(addRegisteredEventHandler({ eventHandler }));
        } else if (data.startsWith('dynamicHeight:')) {
          dispatch(
            setDynamicHeightEnabled({
              isDynamicHeightEnabled: data.slice(14) === 'true',
            })
          );
        } else if (data.startsWith('widgetResizeDuration:')) {
          const widgetResizeDuration = parseInt(data.slice(21), 10);
          dispatch(setWidgetResizeDuration({ widgetResizeDuration }));
        } else if (
          data.startsWith('recalculateRequiredWidgetHeightDueToWidthChange')
        ) {
          dispatch(
            updateRecalculateRequiredWidgetHeight({
              isWidgetHeightRecalculationRequired: true,
            })
          );
        } else if (data.startsWith('minimumWidgetHeight:')) {
          dispatch(
            setMinimumWidgetHeight({
              minimumWidgetHeight: JSON.parse(data.slice(20)),
            })
          );
        } else if (data.startsWith('maximumWidgetHeight:')) {
          const maximumWidgetHeight = JSON.parse(data.slice(20));
          dispatch(setMaximumWidgetHeight({ maximumWidgetHeight }));
        } else if (data.startsWith('autoScroll:')) {
          const isAutoScrollEnabled = data.slice(11) === 'true';
          dispatch(setAutoScrollEnabled({ isAutoScrollEnabled }));
        } else if (data.startsWith('navigation:back')) {
          handleClickBackSideEffects({
            callBackFunction: () => {
              dispatch(
                setShouldNavigateBackward({
                  shouldNavigateBackward: true,
                })
              );
            },
            dispatch,
            isDynamicHeightEnabled,
            isConsentStatementAboveNavigation,
            isPenultimatePage,
            hasStatementGraduallyDisappeared,
            updateHasStatementGraduallyDisappeared,
            minimumWidgetHeight,
            statement,
          });
        } else if (data.startsWith('allInitialMessagesArrived')) {
          dispatch(
            setAllInitialMessagesArrived({
              haveAllInitialMessagesArrived: true,
            })
          );
        } else if (data.startsWith('useCidFromURL:')) {
          const useCidFromURL = data.slice(14) === 'true';
          dispatch(setUseCidFromURL({ useCidFromURL }));
        } else if (data.startsWith('setCookies:')) {
          // Do not capture cookies for partner forms.
          if (!isExternalPartnerForm) {
            const parentPageCookies = JSON.parse(data.slice(11));
            dispatch(setParentPageCookies({ parentPageCookies }));
          }
        } else if (data.startsWith('oneTrustLocale:')) {
          const oneTrustLocale = JSON.parse(data.slice(15));
          dispatch(setOneTrustLocale(oneTrustLocale));
        } else if (data.startsWith('eventPublicKeyString:')) {
          const eventPublicKeyString = data.slice(21);
          dispatch(setEventPublicKeyString({ eventPublicKeyString }));
        } else if (data.startsWith('enableDebugLogging')) {
          updateAndExposeDynamicHeightData({
            isDebugLoggingEnabled: true,
          });
        } else if (data.startsWith('identityUpdate:')) {
          // We don't want to accidentally capture userIdentities for partners
          if (!isExternalPartnerForm) {
            const identityData = JSON.parse(data.slice(15));
            dispatch(updateIdentities(identityData));
          }
        } else if (data.startsWith('componentExperiments:')) {
          const componentExperiments = JSON.parse(data.slice(21));
          dispatch(setComponentExperiments({ componentExperiments }));
        } else if (data.startsWith('isBounceNavigationAnimationEnabled')) {
          dispatch(
            setIsBounceNavigationAnimationEnabled({
              isBounceNavigationAnimationEnabled: true,
            })
          );
        } else if (data.startsWith('anuraData:')) {
          const anuraData = JSON.parse(data.slice(10));
          dispatch(setAnuraData({ anuraData }));
        } else if (data.startsWith('mosaicSubdomainLoading:')) {
          const dataDogMetricPayload = JSON.parse(data.slice(23));
          incrementMetric(dataDogMetricPayload);
        }
      }
    };

    const hashParams = getUrlHashParams();
    if (
      hashParams &&
      hashParams.iFrameId &&
      hasSentBrowserMessagesRegisteredEvent === false
    ) {
      exposeEventToPartner({
        eventType: 'browserMessagesRegistered',
        eventObject: {
          iFrameId: hashParams.iFrameId,
        },
      });

      setHasSentBrowserMessagesRegisteredEvent(true);
    }
  }, []);

  useEffect(() => {
    if (sessionDataPopulationStatus === 'complete') {
      dispatch(setAdvancePageStatus({ advancePageStatus: 'start' }));
    }
  }, [sessionDataPopulationStatus]);

  useEffect(() => {
    if (iFrameId && externalSessionDataJSON) {
      let sessionData = JSON.parse(externalSessionDataJSON);
      // Note: GOLD-710 will need to remove this invocation of extractParentPageTrustedFormOutOfSessionData below
      sessionData = extractParentPageTrustedFormOutOfSessionData(
        sessionData,
        trustedFormConfig.mappedAdmiralQuestionId,
        trustedFormConfig.questionText
      );

      if (Object.keys(sessionData).length > 0) {
        parseSessionDataAndPopulateFormFields({
          sessionData,
          components,
          dispatch,
          iFrameId,
          countryCode,
          formId,
        });
      }
    }
  }, [iFrameId, externalSessionDataJSON]);
};

export default useBrowserMessages;
